import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useAgentSessions } from "contexts/AgentSessions";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UpgradeDialogSource } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import TemplateEditButton from "components/TemplateEditButton";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

interface IProps {
  open: boolean;
  agentSessionId: number | null;
  handleClose: () => void;
  handleBack: () => void;
  handleSubmit: () => void;
}

type TemplateError = {
  idx: number;
  message: string;
};

export default function TemplatesDialog({
  agentSessionId,
  open,
  handleClose,
  handleBack,
  handleSubmit,
}: IProps) {
  const theme = useTheme();
  const selectedAgentSessionId = useAgentSessions();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { setUpgradeDialogSource } = useContext(SubscriptionContext);
  const { setOpenIntegrationDialog } = useContext(UserIntegrationsContext);

  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    fetchOutreachTemplates,
    setCreateTemplateDialogOpen,
    setOnSaveAsNew,
    editTemplateDialogId,
  } = useContext(OutreachTemplatesContext);
  const { emailSettings, currentOrg } = useContext(OrganizationUserContext);
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [templates, setTemplates] = useState<Template[]>([]);
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const [nextLoading, setNextLoading] = useState(false);

  const selectedFollowups = templates?.slice(1, templates?.length);
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );

  const editTemplateDialogIdRef = useRef(editTemplateDialogId);
  editTemplateDialogIdRef.current = editTemplateDialogId;

  const handleSaveAsNew = useCallback((newTemplate: Template) => {
    const editId = Number(editTemplateDialogIdRef.current);
    setTemplates((prev) =>
      prev.map((t) => (t.id === editId ? newTemplate : t)),
    );
  }, []);

  useEffect(() => {
    if (!open) return;
    setOnSaveAsNew(() => handleSaveAsNew);
    return () => setOnSaveAsNew(null);
  }, [handleSaveAsNew, setOnSaveAsNew, open]);

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...templates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = template;
      setTemplates(copy);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...templates];
    copy.splice(index, 1);
    setTemplates(copy);
  };

  const addFollowUp = () => {
    setTemplates([...templates, followupTemplates?.[0]]);
  };

  const initializeEmails = () => {
    if (templates?.length > 0) return;

    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    const presetTemplates = emailSettings?.emailSettingTemplates || [];
    const matchedTemplates = [];

    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push(template);
      }
    }
    if (matchedTemplates?.length > 0) {
      setTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setTemplates([filteredOutreachTemplates[0]]);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
  };

  const handleGenerateTemplate = () => {
    trackEvent("Generate Template Button Clicked", {
      Source: "Agent Chat",
      "Agent Session ID": selectedAgentSessionId,
    });
    setCreateTemplateDialogOpen(true);
  };

  useEffect(() => {
    if (templates.length === 0 && emailSettings?.emailSettingTemplates) {
      initializeEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSettings?.emailSettingTemplates?.length, allTemplates?.length]);

  const handleScheduleEmails = async () => {
    if (!currentOrg?.id) return;

    setNextLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${agentSessionId}/schedule`,
        "POST",
        {},
        {
          templateIds: templates
            .map((t) => t.id)
            .filter((id): id is number => id !== undefined),
        },
        true,
      );

      const jsonMessage = await res.json();

      if (!res.ok) {
        if (jsonMessage.field) {
          if (
            jsonMessage.field === "templates" &&
            jsonMessage.templateIdx !== undefined &&
            jsonMessage.templateIdx !== null
          ) {
            setTemplatesError({
              idx: jsonMessage.templateIdx,
              message: jsonMessage.message,
            });
            return;
          }

          if (jsonMessage.field === "date") {
            if (templates.length >= 5) {
              setTemplatesError({
                idx: 4,
                message:
                  "We are not able to schedule emails so far in advance. Please reduce your number of follow-ups",
              });
              return;
            }
          }

          if (jsonMessage.field === "paywall") {
            if (jsonMessage.message.includes("remaining this month")) {
              setUpgradeDialogSource(UpgradeDialogSource.hits_limit);
            } else {
              setUpgradeDialogSource(UpgradeDialogSource.campaigns_limit);
            }
            return;
          }
        } else if (jsonMessage.message?.includes("Google integration")) {
          setOpenIntegrationDialog(true);
          return;
        }
        setAlert(jsonMessage.message, "error");
      } else {
        handleSubmit();
        handleClose();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setNextLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isMobileScreen}
      maxWidth="md"
    >
      <DialogTitle component="div">
        <Typography variant="h6">Select your Templates</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Typography color="textSecondary">
              Select the templates you want to use for pitching to these brands.
            </Typography>
            <Grid container sx={{ my: 2 }} columnSpacing={0.5}>
              <Grid item xs={12} sm={3}>
                <Typography sx={{ mt: 0.75 }}>Outreach Template</Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                {templates[0] ? (
                  <OutreachSelect
                    templates={templates}
                    handleChange={handleTemplateChange}
                    filteredOutreachTemplates={filteredOutreachTemplates}
                    templateFromEmailSettings={templateFromEmailSettings}
                  />
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleGenerateTemplate}
                    sx={{ height: 25, mt: 0.4, textTransform: "none" }}
                    disableElevation
                  >
                    Generate Template
                  </Button>
                )}
                {templatesError && templatesError.idx === 0 && (
                  <Grid container>
                    <Typography
                      variant="caption"
                      sx={{ fontStyle: "italic", color: "error.main" }}
                    >
                      {templatesError.message}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 2 }} columnSpacing={{ xs: 0, sm: 0.5 }}>
              <Grid item xs={12} sm={3}>
                <Typography
                  sx={{ mt: selectedFollowups.length > 0 ? 0.75 : 0.5 }}
                >
                  Follow-up Templates
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                {selectedFollowups?.map((followup, index) => (
                  <Grid container sx={{ mb: 1 }} key={index}>
                    <Grid container alignItems="center">
                      <Select
                        onChange={(e) => handleTemplateChange(e, index + 1)}
                        value={followup?.id}
                        size="small"
                      >
                        {filteredFollowUps?.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            <Box>
                              {template.name}
                              {template.id &&
                                templateFromEmailSettings?.includes(
                                  template.id,
                                ) && (
                                  <Chip
                                    label="Last Used"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      fontSize: 10,
                                      backgroundColor: "primary.light",
                                    }}
                                  />
                                )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>

                      {followup?.id && (
                        <TemplateEditButton
                          sx={{ ml: 1 }}
                          templateId={followup.id}
                        />
                      )}

                      <IconButton
                        sx={{ fontSize: 13, color: grey[500] }}
                        onClick={() => removeFollowUp(index + 1)}
                      >
                        <Box component="i" className="fa-regular fa-trash" />
                      </IconButton>
                    </Grid>
                    {templatesError && templatesError.idx === index + 1 && (
                      <Grid container>
                        <Typography
                          variant="caption"
                          sx={{ fontStyle: "italic", color: "error.main" }}
                        >
                          {templatesError.message}
                        </Typography>
                      </Grid>
                    )}
                    <Grid container>
                      <Typography
                        variant="caption"
                        sx={{ fontStyle: "italic", fontSize: 10 }}
                        color="textSecondary"
                      >
                        Sent 4 business days after previous email
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
                {followupTemplates?.length > 0 &&
                  templates?.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                    <Grid container alignItems="center">
                      <Grid item xs md>
                        <Button
                          disableElevation
                          onClick={addFollowUp}
                          sx={{
                            fontSize: 12,
                            textTransform: "none",
                            px: 0,
                          }}
                        >
                          + Follow Up
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                {followupTemplates.length === 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleGenerateTemplate}
                    sx={{
                      height: 25,
                      mt: 0.4,
                      textTransform: "none",
                    }}
                    disableElevation
                  >
                    Generate Follow-up
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleBack}>
          Back
        </Button>
        <LoadingButton
          loading={nextLoading}
          color="primary"
          variant="contained"
          disableElevation
          onClick={() => {
            handleScheduleEmails();
          }}
        >
          Continue
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
